import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Buscador, Chip, Filtro, IconoMas, Listado, Menu, useBusquedaDiferida, type Columna, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { EliminarCliente } from './EliminarCliente';
import { ImportarClientes } from './ImportarClientes';
import {
  exportar,
  useCambiosCliente,
  useClientes,
  version,
  type Cliente,
  type FiltrosClientes,
  type FormatoExportacion,
} from './datos';

// El listado de clientes (#104): «Clientes - Listado» del Figma y sus variantes
// (Sin resultados) y (Sin clientes). Sigue a `ListadoSucursales.tsx`, que es la
// pantalla de muestra: columnas declaradas, menú por fila con solo lo que se
// puede hacer, avisos abajo y errores arriba.
//
// Lo propio de clientes:
//
//   - **los filtros viven en la URL** (`/clientes?buscar=…&tipo=company`), para
//     que un enlace o un «Atrás» desde la edición los conserven. Es lo que pide
//     #104. El issue dice «como en sucursales», pero sucursales los guarda en el
//     estado de la pantalla: se siguió el objetivo del issue, y Clientes es la
//     primera pantalla que lo hace. El cursor de la página no va en la URL —la
//     pila de «Anterior» no cabe en un enlace—, así que un enlace abre la
//     primera página con los filtros puestos;
//   - **un solo buscador** para nombre, código, documento, teléfono y correo
//     (Figma del 2026-10-06; antes correo y teléfono tenían cada uno su caja).
//     Teléfono y correo se comparan completos: los resuelve la API, que mira
//     si lo escrito parece uno u otro;
//   - Activar / Desactivar va en el menú de la fila, con `PUT …/status` y la
//     versión del listado, sin abrir la edición;
//   - tocar una fila abre la ficha (#105); la edición queda en el menú de la
//     fila. El nombre es además un botón, para llegar con el teclado.

/** Los filtros de la URL. Los parámetros se llaman como los campos: `buscar`, `tipo`… */
type FiltrosUrl = Omit<FiltrosClientes, 'cursor'>;

/**
 * Los filtros que trae la URL. Un valor que no se reconoce —un enlace viejo o
 * editado a mano— cuenta como «Todos», no como error.
 */
function leerFiltros(params: URLSearchParams): FiltrosUrl {
  const tipo = params.get('tipo');
  const estado = params.get('estado');
  return {
    buscar: params.get('buscar') ?? '',
    tipo: tipo === 'person' || tipo === 'company' ? tipo : 'todos',
    estado: estado === 'activos' || estado === 'inactivos' ? estado : 'todos',
  };
}

export function ListadoClientes() {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosCliente();
  useTituloPantalla(t('admin.clientes.titulo'));

  const [params, setParams] = useSearchParams();
  const url = leerFiltros(params);
  const busqueda = params.toString();

  const [texto, setTexto] = useState(url.buscar);
  const [cursor, setCursor] = useState<string | null>(null);
  const [importando, setImportando] = useState(false);
  // Por dónde se pasó: la API solo sabe ir hacia adelante, así que volver es
  // sacar el último cursor de esta pila.
  const [anteriores, setAnteriores] = useState<(string | null)[]>([]);
  const [aEliminar, setAEliminar] = useState<Cliente | null>(null);
  const { hacer, problema, setProblema } = useAccionDeFila();

  // Cuando la URL cambia —un filtro de esta pantalla, «Atrás», o tocar «Clientes»
  // en el menú, que la deja limpia— se vuelve a la primera página: el cursor de
  // la búsqueda anterior no significa nada en la nueva. Y si cambió el texto sin
  // que se escribiera acá, la caja lo muestra. Se ajusta al dibujar, comparando
  // con la URL anterior, y no en un efecto que dibujaría primero lo viejo.
  const [vista, setVista] = useState({ busqueda, buscar: url.buscar });
  if (busqueda !== vista.busqueda) {
    setVista({ busqueda, buscar: url.buscar });
    setCursor(null);
    setAnteriores([]);
    if (url.buscar !== vista.buscar) setTexto(url.buscar);
  }

  /**
   * Pone filtros en la URL. Reemplaza la entrada del historial en vez de sumar
   * una: «Atrás» sale del listado, no deshace un filtro por cada tecla.
   */
  function filtrar(cambio: Partial<FiltrosUrl>) {
    setParams(
      (actuales) => {
        const nuevos = new URLSearchParams(actuales);
        for (const [clave, valor] of Object.entries(cambio)) {
          // «Todos» es el valor de los combos; en el buscador es un texto como cualquiera.
          if (valor && (clave === 'buscar' || valor !== 'todos')) nuevos.set(clave, valor);
          else nuevos.delete(clave);
        }
        return nuevos;
      },
      { replace: true },
    );
  }

  useBusquedaDiferida(texto, (buscar) => filtrar({ buscar }), { pendiente: texto !== url.buscar });

  const filtros: FiltrosClientes = { ...url, cursor };
  const consulta = useClientes(filtros);
  const pagina = consulta.data;

  // `desdeListado`: el formulario vuelve con «Atrás» y no a `/clientes` limpio,
  // para que regresar conserve los filtros de la URL.
  const editar = (c: Cliente) =>
    void navegar(`/clientes/${String(c.id)}/editar`, { state: { desdeListado: true } });
  // `desdeListado`: si el cliente se elimina desde su edición, se vuelve hasta acá,
  // con los filtros, en vez de a una ficha que ya no existe.
  const verFicha = (c: Cliente) => void navegar(`/clientes/${String(c.id)}`, { state: { desdeListado: true } });

  function opciones(c: Cliente): ElementoMenu[] {
    const id = String(c.id);
    const nombre = { nombre: c.name };

    return [
      { etiqueta: t('admin.clientes.menu.verFicha'), onSeleccionar: () => verFicha(c) },
      { etiqueta: t('admin.clientes.menu.editar'), onSeleccionar: () => editar(c) },
      c.isActive
        ? {
            etiqueta: t('admin.clientes.menu.desactivar'),
            onSeleccionar: () =>
              void hacer(
                cambios.cambiarEstado(id, version(c.version), false),
                t('admin.clientes.aviso.desactivado', nombre),
              ),
          }
        : {
            etiqueta: t('admin.clientes.menu.activar'),
            onSeleccionar: () =>
              void hacer(
                cambios.cambiarEstado(id, version(c.version), true),
                t('admin.clientes.aviso.activado', nombre),
              ),
          },
      { tipo: 'divisor' as const },
      {
        etiqueta: t('admin.clientes.menu.eliminar'),
        peligro: true,
        onSeleccionar: () => setAEliminar(c),
      },
    ];
  }

  // Anchos de «Clientes - Listado (Más acciones)» del Figma. Nombre no lleva:
  // toma lo que sobra, y en pantallas anchas no deja un hueco al final.
  const columnas: Columna<Cliente>[] = [
    {
      id: 'codigo',
      titulo: t('admin.clientes.columna.codigo'),
      ancho: 100,
      celda: (c) => c.code ?? '—',
      tarjeta: 'pie',
    },
    {
      id: 'nombre',
      titulo: t('admin.clientes.columna.nombre'),
      // El nombre comercial de una empresa (#126), en gris debajo de la razón
      // social: la fila no crece, y se reconoce por el nombre con que la buscan.
      celda: (c) =>
        c.tradeName ? (
          <>
            <button type="button" className="tipos__enlace clientes__nombre" onClick={() => verFicha(c)}>
              {c.name}
            </button>
            <span className="clientes__comercial">{c.tradeName}</span>
          </>
        ) : (
          <button type="button" className="tipos__enlace" onClick={() => verFicha(c)}>
            {c.name}
          </button>
        ),
      tarjeta: 'titulo',
    },
    {
      id: 'tipo',
      titulo: t('admin.clientes.columna.tipo'),
      ancho: 95,
      // Persona en guinda y Empresa en gris, como en el Figma: se distinguen
      // de un vistazo sin leer la palabra.
      celda: (c) =>
        c.kind === 'company' ? (
          <Chip>{t('admin.clientes.empresa')}</Chip>
        ) : (
          <Chip tono="marca">{t('admin.clientes.persona')}</Chip>
        ),
    },
    {
      id: 'documento',
      titulo: t('admin.clientes.columna.documento'),
      ancho: 160,
      // «DUI 04512345-6»: el número solo no dice de qué documento es.
      celda: (c) =>
        c.docIdentifier ? [c.docTypeCode, c.docIdentifier].filter(Boolean).join(' ') : '—',
      tarjeta: 'pie',
    },
    {
      id: 'telefono',
      titulo: t('admin.clientes.columna.telefono'),
      ancho: 100,
      celda: (c) => c.primaryPhone ?? '—',
      tarjeta: 'renglon',
    },
    {
      id: 'correo',
      titulo: t('admin.clientes.columna.correo'),
      ancho: 160,
      celda: (c) => c.primaryEmail ?? '—',
    },
    {
      id: 'vehiculos',
      titulo: t('admin.clientes.columna.vehiculos'),
      ancho: 80,
      alinear: 'fin',
      celda: (c) => c.assetCount,
    },
    {
      id: 'estado',
      titulo: t('admin.clientes.columna.estado'),
      ancho: 90,
      celda: (c) =>
        c.isActive ? (
          <Chip tono="exito">{t('admin.clientes.activo')}</Chip>
        ) : (
          <Chip>{t('admin.clientes.inactivo')}</Chip>
        ),
      tarjeta: 'esquina',
    },
  ];

  const botonNuevo = (
    <Boton
      icono={<IconoMas />}
      onClick={() => void navegar('/clientes/nuevo', { state: { desdeListado: true } })}
    >
      {t('admin.clientes.nuevo')}
    </Boton>
  );

  /** Baja lo que muestra el listado, con los filtros de la URL. */
  async function bajar(formato: FormatoExportacion) {
    setProblema(null);
    try {
      const fallo = await exportar(formato, url);
      if (fallo) setProblema(fallo.code ? traducirError(fallo.code, t) : t('ui.formulario.aviso.sinRespuesta'));
    } catch {
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
    }
  }

  // «Más acciones» del Figma, igual que en Productos/Servicios: importar abre su
  // ventana; exportar baja el archivo directo, con los filtros puestos. Excel,
  // CSV y PDF, los mismos tres formatos que el catálogo (#107).
  const masAcciones: ElementoMenu[] = [
    { etiqueta: t('admin.clientes.menu.importarArchivo'), onSeleccionar: () => setImportando(true) },
    { tipo: 'divisor' },
    { tipo: 'grupo', etiqueta: t('admin.clientes.exportar.grupo') },
    { etiqueta: t('admin.clientes.exportar.aExcel'), onSeleccionar: () => void bajar('xlsx') },
    { etiqueta: t('admin.clientes.exportar.aCsv'), onSeleccionar: () => void bajar('csv') },
    { etiqueta: t('admin.clientes.exportar.aPdf'), onSeleccionar: () => void bajar('pdf') },
    { tipo: 'nota', texto: t('admin.clientes.exportar.nota') },
  ];

  // Una página que no es la primera cuenta como filtro: si queda vacía —se
  // eliminó su última fila—, el taller no está vacío, y decirle «Aún no hay
  // clientes» sería falso. Así sale «sin resultados», que vuelve al inicio.
  const hayFiltros =
    filtros.buscar !== '' || filtros.tipo !== 'todos' || filtros.estado !== 'todos' || filtros.cursor !== null;

  // «Sin clientes» del Figma: sin nada que buscar, la barra no lleva buscador
  // ni filtros, solo los botones.
  // Solo con la respuesta de estos filtros, no con la anterior que se muestra
  // mientras carga: al borrar una búsqueda sin resultados, esa respuesta vieja
  // vacía haría desaparecer el buscador —y su foco— mientras se escribe.
  const sinClientes =
    consulta.isSuccess && !consulta.isPlaceholderData && (pagina?.items.length ?? 0) === 0 && !hayFiltros;

  /**
   * «Ningún cliente coincide con «Talleres Roca» y el filtro Tipo: Persona.», como
   * en el Figma. Los combos se nombran como se ven en la barra.
   */
  function detalleSinResultados(): string {
    const combos = [
      filtros.tipo !== 'todos' &&
        `${t('admin.clientes.filtro.tipo')}: ${t(filtros.tipo === 'company' ? 'admin.clientes.empresa' : 'admin.clientes.persona')}`,
      filtros.estado !== 'todos' &&
        `${t('admin.clientes.filtro.estado')}: ${t(filtros.estado === 'activos' ? 'admin.clientes.activo' : 'admin.clientes.inactivo')}`,
    ].filter((c): c is string => typeof c === 'string');

    return combos.length === 0
      ? t('admin.clientes.sinResultados', { texto: filtros.buscar })
      : t('admin.clientes.sinResultados.conFiltros', {
          n: combos.length,
          texto: filtros.buscar,
          filtros: new Intl.ListFormat(idioma, { type: 'conjunction' }).format(combos),
        });
  }

  /** Qué hacer cuando nada coincide, según qué haya puesto. */
  function sugerenciaSinResultados(): string {
    const hayCombos = filtros.tipo !== 'todos' || filtros.estado !== 'todos';
    if (filtros.buscar && hayCombos) return t('admin.clientes.sinResultados.buscarYCombos');
    // Teléfono y correo se comparan completos: un pedazo no encuentra a nadie.
    if (filtros.buscar) return t('admin.clientes.sinResultados.buscar');
    if (hayCombos) return t('admin.clientes.sinResultados.combos');
    // Sin filtros, solo una página que no es la primera: se eliminó su última fila.
    return t('admin.clientes.sinResultados.pagina');
  }

  return (
    <>
      {problema && (
        <AvisoError aviso={{ titulo: t('admin.clientes.error.titulo'), texto: problema }} />
      )}

      <Listado
        titulo={t('admin.clientes.titulo')}
        info={t('admin.clientes.info')}
        columnas={columnas}
        filas={pagina?.items}
        claveFila={(c) => String(c.id)}
        estado={consulta.isPending ? 'cargando' : consulta.isError ? 'error' : 'listo'}
        onReintentar={() => void consulta.refetch()}
        filtros={
          !sinClientes && (
          <>
            <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.clientes.buscar')} />
            <Filtro
              etiqueta={t('admin.clientes.filtro.tipo')}
              valor={filtros.tipo}
              onCambiar={(valor) => filtrar({ tipo: valor as FiltrosClientes['tipo'] })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.clientes.filtro.todos') },
                { valor: 'person', etiqueta: t('admin.clientes.persona') },
                { valor: 'company', etiqueta: t('admin.clientes.empresa') },
              ]}
            />
            <Filtro
              etiqueta={t('admin.clientes.filtro.estado')}
              valor={filtros.estado}
              onCambiar={(valor) => filtrar({ estado: valor as FiltrosClientes['estado'] })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.clientes.filtro.todos') },
                { valor: 'activos', etiqueta: t('admin.clientes.activo') },
                { valor: 'inactivos', etiqueta: t('admin.clientes.inactivo') },
              ]}
            />
          </>
          )
        }
        acciones={
          <>
            <Menu
              ancho={230}
              elementos={masAcciones}
              disparador={({ alternar, atributos }) => (
                <Boton variante="secundario" soloIcono aria-label={t('admin.clientes.mas')} onClick={alternar} {...atributos}>
                  •••
                </Boton>
              )}
            />
            {botonNuevo}
          </>
        }
        hayFiltros={hayFiltros}
        onLimpiarFiltros={() => {
          setTexto('');
          // La primera página aunque la URL ya estuviera limpia: limpiar desde
          // una página 2 vacía también tiene que volver al inicio.
          setCursor(null);
          setAnteriores([]);
          setParams({}, { replace: true });
        }}
        // «Sin clientes» del Figma: alta e importación a la vista, y la pista
        // para quien llega con la lista en un Excel.
        vacio={{
          titulo: t('admin.clientes.vacio.titulo'),
          texto: t('admin.clientes.vacio.texto'),
          acciones: (
            <>
              {botonNuevo}
              <Boton variante="secundario" onClick={() => setImportando(true)}>
                {t('admin.clientes.menu.importarArchivo')}
              </Boton>
            </>
          ),
          nota: t('admin.clientes.vacio.nota'),
        }}
        // «Sin resultados»: el título es el de `Listado`, más la línea con lo que
        // se buscó —y los combos puestos— y una sugerencia que dice la verdad
        // sobre lo que está puesto. «Revisa la ortografía» solo si se escribió
        // algo en el buscador.
        {...(filtros.buscar && { detalleSinResultados: detalleSinResultados() })}
        sugerenciaSinResultados={sugerenciaSinResultados()}
        opcionesFila={opciones}
        onFila={verFicha}
        paginacion={{
          hayAnterior: anteriores.length > 0,
          hayMas: pagina?.hasMore ?? false,
          onAnterior: () => {
            setCursor(anteriores.at(-1) ?? null);
            setAnteriores((a) => a.slice(0, -1));
          },
          onSiguiente: () => {
            if (!pagina?.nextCursor) return;
            setAnteriores((a) => [...a, cursor]);
            setCursor(pagina.nextCursor);
          },
        }}
      />

      {/* La fila como está ahora, no como estaba al abrir el menú: si otro la
          cambió, la ventana desactiva con la versión nueva tras recargar (#177). */}
      <EliminarCliente
        cliente={aEliminar && (pagina?.items.find((c) => c.id === aEliminar.id) ?? aEliminar)}
        onCerrar={() => setAEliminar(null)}
      />
      <ImportarClientes abierta={importando} onCerrar={() => setImportando(false)} />
    </>
  );
}
